import {Injectable} from '@angular/core';
import {DoorLoader} from './door.loader';
import {Door, DoorType} from '../../models/door';
import {map, Observable} from 'rxjs';
import {
  DoorFragment,
  DoorType as DoorTypeGQL,
  GetVisualDoorGQL, GetVisualDoorQuery,
  GetVisualDoorsStateServiceGQL,
  GetVisualDoorsStateServiceQuery,
  PaginationMode
} from '../../../generated/graphql';
import {LightPosition} from '../../models/scenario-image';
import {GenericFilter, transformGenericFilterIntoFilterInput} from "../filter/filter.service";
import {mapDoorTypeFromGraphQlToIntern} from "../../utils/door-type-mapping";
import {Product} from "../../models/product";

export interface CursorBasedPaginationInfo {
  hasNextPage: boolean;
  hasPreviousPage?: boolean;
  startCursor?: string;
  endCursor?: string;
}

export interface PaginationInfo {
  page: number;
  pageSize: number;
  totalPages: number;
  totalResults: number;
}

export interface PaginationInformation {
  paginationMode: PaginationMode,
  paginationInfo?: PaginationInfo,
  cursorBasedPaginationInfo?: CursorBasedPaginationInfo
}

@Injectable({providedIn: 'root'})
export class GraphqlDoorLoader extends DoorLoader {
  constructor(private readonly getVisualDoorsQuery: GetVisualDoorsStateServiceGQL,
              private readonly getVisualDoorQuery: GetVisualDoorGQL) {
    super();
  }

  private initialDoorLoadCount = 12 * 2.5

  getDoors(customerKey: string, filter: GenericFilter): Observable<Array<Product>> {

    return new Observable<Array<Product>>(sub => {
      let currentPage = 1;
      let afterCursor: undefined | string = undefined
      const combinedResult: Array<Product> = [];

      const call = () => {
        if (sub.closed) {
          sub.complete();
          return;
        }

        this.getDoorsPaginated(customerKey, filter, {page: currentPage, afterCursor, mode: PaginationMode.Page}).subscribe({
          next: response => {
            combinedResult.push(...response.result);
            sub.next(combinedResult);

            if (response.paginationMode === PaginationMode.Page) {
              currentPage++;

              if (currentPage <= response.paginationInfo?.totalPages!) {
                setTimeout(() => {
                  call();
                }, 100);
              } else {
                sub.complete();
              }
            } else {
              afterCursor = response.cursorBasedPaginationInfo?.endCursor

              if (response.cursorBasedPaginationInfo?.hasNextPage) {
                setTimeout(() => {
                  call();
                }, 100);
              } else {
                sub.complete();
              }
            }
          },
          error: err => {
            sub.error(err);
            sub.complete();
          }
        });
      };
      call();
    });
  }

  private mapDoorToProduct(door: Door): Product {
    return {
      id: door.id,
      externalIds: door.externalIds,
      visualisationImageUrl: door.imageUrl,
      thumbnailUrl: door.thumbnailUrl,
      productType: 'door',
      customData: door.customData,
      name: door.name,
      price: door.price,
      link: door.productPageUrl,
      enabled: true,
      visualisationLayer: undefined,
      renderingContextFilter: undefined,
      renderingContextFillStyle: undefined,

      // Properties from door
      shadowImageUrls: door.shadowImageUrls,
      isFavorite: door.isFavorite,
      doorType: door.doorType
    }
  }

  getDoorsPaginated(customerKey: string, filter: GenericFilter, paginationArgs: { page?: number, afterCursor?: string, mode: PaginationMode }):
    Observable<
      {
        result: Array<Product>
      } & PaginationInformation> {

    const transformedFilter = transformGenericFilterIntoFilterInput(filter)

    return this.getVisualDoorsQuery.fetch({
      customerKey,
      filter: transformedFilter,
      paginationModel: {
        mode: paginationArgs.mode,
        page: paginationArgs.page,
        afterCursor: paginationArgs.afterCursor,
        pageSize: this.initialDoorLoadCount,
      },
    }, {
      fetchPolicy: 'no-cache'
    }).pipe(map(result => {

      const data = result.data.getVisualDoors

      if (!data.results) {
        return {
          totalPages: 0,
          paginationMode: PaginationMode.Page,
          result: []
        };
      }

      return {
        paginationMode: data.paginationMode,
        cursorBasedPaginationInfo: this.mapCursorBasedPaginationInfo(data.cursorBasedPaginationInfo),
        paginationInfo: this.mapPageInfo(data.pageInfo),
        result: data.results.map(visualDoor => {
          const mapped = this.mapVisualDoor(visualDoor)
          return this.mapDoorToProduct(mapped)
        })
      }
    }))
  }

  mapCursorBasedPaginationInfo(graphQlPageInfo?: GetVisualDoorsStateServiceQuery['getVisualDoors']['cursorBasedPaginationInfo']): CursorBasedPaginationInfo | undefined {
    if (!graphQlPageInfo) {
      return undefined
    }
    return {
      endCursor: graphQlPageInfo.endCursor || undefined,
      hasNextPage: graphQlPageInfo.hasNextPage,
      hasPreviousPage: graphQlPageInfo?.hasPreviousPage || undefined,
      startCursor: graphQlPageInfo?.startCursor || undefined,
    }
  }

  mapPageInfo(graphQlPageInfo?: GetVisualDoorsStateServiceQuery['getVisualDoors']['pageInfo']): PaginationInfo | undefined {
    if (!graphQlPageInfo) {
      return undefined
    }
    return {
      totalPages: graphQlPageInfo.totalPages,
      page: graphQlPageInfo.page,
      pageSize: graphQlPageInfo.pageSize,
      totalResults: graphQlPageInfo.totalResults,
    }
  }

  mapVisualDoor(visualDoor: DoorFragment & { shadowProfile?: GetVisualDoorQuery['getVisualDoorForExternalIdAndCustomer']['shadowProfile']}): Door {
    // Map the light position
    const shadowImageUrls: Map<LightPosition, string> = new Map<LightPosition, string>();
    if (visualDoor.shadowProfile?.northShadowImage) {
      shadowImageUrls.set(LightPosition.CENTER, visualDoor.shadowProfile.northShadowImage.url);
    }
    if (visualDoor.shadowProfile?.northWestShadowImage) {
      shadowImageUrls.set(LightPosition.LEFT_1, visualDoor.shadowProfile.northWestShadowImage.url);
    }
    if (visualDoor.shadowProfile?.westShadowImage) {
      shadowImageUrls.set(LightPosition.LEFT_2, visualDoor.shadowProfile.westShadowImage.url);
    }
    if (visualDoor.shadowProfile?.northEastShadowImage) {
      shadowImageUrls.set(LightPosition.RIGHT_1, visualDoor.shadowProfile.northEastShadowImage.url);
    }
    if (visualDoor.shadowProfile?.eastShadowImage) {
      shadowImageUrls.set(LightPosition.RIGHT_2, visualDoor.shadowProfile.eastShadowImage.url);
    }

    return {
      id: visualDoor.id,
      imageUrl: visualDoor.image.url,
      thumbnailUrl: visualDoor.thumbnail.url,
      productPageUrl: visualDoor.showNowLink,
      externalIds: visualDoor.externalIds,
      doorType: this.mapDoorType(visualDoor.doorType)!,
      customData: visualDoor.customData || undefined,
      shadowImageUrls,
      isFavorite: visualDoor.isFavorite,
      name: visualDoor.name,
      price: visualDoor.price !== null ? visualDoor.price : undefined,
      productType: 'door'
    };
  }

  mapDoorType(type: DoorTypeGQL | undefined | null): DoorType | undefined {
    return mapDoorTypeFromGraphQlToIntern(type)
  }

  getDoor(customerKey: string, externalDoorId: string): Observable<Product> {
    return this.getVisualDoorQuery.fetch({
      customerKey,
      externalId: externalDoorId,
    }, {
      context: {
        ignoreDefaultErrorHandling: true
      }
    }).pipe(map(result => {
      const mapped = this.mapVisualDoor(result.data.getVisualDoorForExternalIdAndCustomer);
      return this.mapDoorToProduct(mapped)
    }));
  }
}
